import { Card, CardContent, CardHeader, Grid, CircularProgress } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { TextField, Button, Tooltip, Typography } from '@material-ui/core';
import ChevronRight from '@material-ui/icons/ChevronRight';
import PropTypes from 'prop-types';
import React, { useState, useContext, useEffect } from 'react';
import { withRouter } from 'react-router';
import { OrderReviewContext } from './OrderReviewContext';
import { AppContext } from '../../AppContext';
import { Autocomplete } from '../Autocomplete';
import cn from 'classnames';
import { createOrderRecommendation, updateOrderRecommendation, removeOrderRecommendationById } from '../../services';

const InfoTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    // fontSize: theme.typography.pxToRem(12),
    fontSize: 16,
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const propTypes = {
  classes: PropTypes.object.isRequired,
  isCancelled: PropTypes.bool,
};

/**
 * @func
 * @desc finds all category and library IDs that have an active recommendation associated to them (so it can be shown on the UI)
 * @param {*} activeRecommendations - all currently active order recommendations
 * @param {*} libraries - all available libraries
 */
const findCategoriesAndLibrariesWithActiveRecommendation = (activeRecommendations, libraries) => {
  // find libraries
  const activeLibraryIds = activeRecommendations.reduce((acc, cur) => {
    if (!acc.includes(cur.recommendationLibraryId)) {
      acc.push(cur.recommendationLibraryId);
    }
    return acc;
  }, []);

  const activeCategories = activeLibraryIds.map(libId => {
    return libraries.find(library => library.recommendationLibraryId === libId).recommendationCategoryId;
  });

  return {
    libraries: activeLibraryIds,
    categories: activeCategories,
  };
};

const OrderRecommendations = ({ classes }) => {
  const { state } = useContext(OrderReviewContext);

  const {
    orderLineId,
    orderRecommendationData: {
      recommendationLibraries,
      recommendationTypes,
      recommendationCategories,
      activeRecommendations: initialActiveRecommendations,
    },
  } = state;

  // need activeRecommendations to change
  // let {
  //   orderRecommendationData: { activeRecommendations },
  // } = state;

  const recommendationFormReset = {
    orderLineId,
    recommendationLibraryId: null,
    recommendationTypeId: null,
    recommendation: '',
  };

  const { dispatch } = useContext(AppContext);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedLibraryId, setSelectedLibraryId] = useState(null);
  const [categoryLibraries, setCategoryLibraries] = useState([]);
  const [recommendationForm, setRecommendationForm] = useState(recommendationFormReset);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [activeRecommendations, setActiveRecommendations] = useState(initialActiveRecommendations);
  const [categoriesWithActiveRecommendation, setCategoriesWithActiveRecommendation] = useState(
    findCategoriesAndLibrariesWithActiveRecommendation(activeRecommendations, recommendationLibraries).categories,
  );
  const [librariesWithActiveRecommendation, setLibrariesWithActiveRecommendation] = useState(
    findCategoriesAndLibrariesWithActiveRecommendation(activeRecommendations, recommendationLibraries).libraries,
  );

  const recommendationTypesOptions = recommendationTypes.map(({ recommendationTypeId, code }) => ({
    label: code,
    value: recommendationTypeId,
  }));

  const renderCategoryTile = ({ name, recommendationCategoryId }, index) => {
    const hasRecommendation = categoriesWithActiveRecommendation.includes(recommendationCategoryId);

    return (
      <div
        key={`category-${recommendationCategoryId}`}
        className={cn(
          classes.columnTile,
          recommendationCategoryId === selectedCategoryId && classes.columnTileSelected,
          hasRecommendation && classes.columnTileHasOrderRecommendation,
        )}
        onClick={() => setSelectedCategoryId(recommendationCategoryId)}
      >
        <span>{name}</span>
        <ChevronRight />
      </div>
    );
  };

  const renderLibraryTile = ({ code, recommendationLibraryId }, index) => {
    const hasRecommendation = librariesWithActiveRecommendation.includes(recommendationLibraryId);

    return (
      <div
        key={`library-${recommendationLibraryId}`}
        className={cn(
          classes.columnTile,
          recommendationLibraryId === selectedLibraryId && classes.columnTileSelected,
          hasRecommendation && classes.columnTileHasOrderRecommendation,
        )}
        onClick={() => setSelectedLibraryId(recommendationLibraryId)}
      >
        <span>{code}</span>
        <ChevronRight />
      </div>
    );
  };

  const renderExistingRecommendationListItem = (
    { orderRecommendationId, recommendationLibraryId, recommendationTypeId },
    index,
  ) => {
    const library = recommendationLibraries.find(lib => lib.recommendationLibraryId === recommendationLibraryId);
    const categoryId = recommendationLibraries.find(lib => lib.recommendationLibraryId === recommendationLibraryId)
      .recommendationCategoryId;

    const category = recommendationCategories.find(cat => cat.recommendationCategoryId === categoryId);
    const recommendationObj = activeRecommendations.find(ar => ar.recommendationLibraryId === recommendationLibraryId);
    const type = recommendationTypes.find(type => type.recommendationTypeId === recommendationTypeId);

    return (
      <div
        key={`existing-${recommendationLibraryId}`}
        className={cn(classes.activeRecommendationListItem)}
        // onClick={() => setSelectedLibraryId(recommendationLibraryId)}
      >
        {/* category */}
        <div className={classes.activeRecommendationListItemColumn}>{category.name}</div>

        {/* library */}
        <div className={classes.activeRecommendationListItemColumn}>{library.code}</div>

        {/* type */}
        <div className={classes.activeRecommendationListItemColumn}>{type.description}</div>

        {/* recommendation (with tooltip) */}
        <div className={classes.activeRecommendationListItemColumn}>
          {/* <InfoTooltip
            interactive // doesn't close if the tooltip is hovered over
            title={recommendationObj.recommendation}
            // className={classes.activeRecommendationListItemColumn}
          > */}
          <div className={classes.recommendationTooltipContent}>{recommendationObj.recommendation}</div>
          {/* </InfoTooltip> */}
        </div>
      </div>
    );
  };

  const onUpdateSuccess = async result => {
    // add category and library ids to the running totals to display active recommendations, if they aren't already in there
    setLibrariesWithActiveRecommendation(librariesWithActiveRecommendation.concat(result.recommendationLibraryId));

    const newCatId = recommendationLibraries.find(lib => lib.recommendationLibraryId === result.recommendationLibraryId)
      .recommendationCategoryId;

    if (!categoriesWithActiveRecommendation.includes(newCatId)) {
      setCategoriesWithActiveRecommendation(categoriesWithActiveRecommendation.concat(newCatId));
    }

    // add this new recommendation to the active list if it does not exist, or add it
    const activeRecommendationIndex = activeRecommendations.findIndex(
      ar => ar.recommendationLibraryId === result.recommendationLibraryId,
    );
    if (activeRecommendationIndex > -1) {
      const updatedActiveRecommendations = activeRecommendations.slice();

      updatedActiveRecommendations[activeRecommendationIndex] = result;
      // activeRecommendations[activeRecommendationIndex] = result;

      setActiveRecommendations(updatedActiveRecommendations);
    } else {
      setActiveRecommendations(activeRecommendations.concat(result));
    }

    // update the form values so that the submit function will call PUT next time
    // setRecommendationForm({
    //   // orderRecommendationId: result.orderRecommendationId,
    //   ...result,
    //   ...recommendationForm,
    // });
    // loadRecommendationForm();
  };

  const onRemoveSuccess = ({ recommendationLibraryId }) => {
    // remove library id to the running totals to display active recommendations, if they aren't already in there
    const updatedLibrariesWithActiveRecommendation = librariesWithActiveRecommendation.filter(
      lib => lib !== recommendationLibraryId,
    );

    // find the category id for this library
    const catId = recommendationLibraries.find(lib => lib.recommendationLibraryId === recommendationLibraryId)
      .recommendationCategoryId;

    // check if the updated active libraries still includes at least one library from this category
    const catActive = categoryLibraries.find(cl =>
      updatedLibrariesWithActiveRecommendation.includes(cl.recommendationLibraryId),
    );

    // if the category should no longer be active, remove it from the list
    if (!catActive) {
      setCategoriesWithActiveRecommendation(categoriesWithActiveRecommendation.filter(c => c !== catId));
    }

    setLibrariesWithActiveRecommendation(updatedLibrariesWithActiveRecommendation);

    const updatedActiveRecommendations = activeRecommendations.filter(
      ar => ar.recommendationLibraryId !== recommendationLibraryId,
    );

    // filter out of the active recommendations array
    setActiveRecommendations(updatedActiveRecommendations);

    // update the form values so that the submit function will call PUT next time
    // setRecommendationForm({
    //   // orderRecommendationId: result.orderRecommendationId,
    //   // ...result,
    //   ...recommendationForm,
    // });

    // setSelectedLibraryId(null);
    loadRecommendationForm();
  };

  const createOrderRecommendationAsync = async () => {
    //
    try {
      setIsSaving(true);

      // try to create the recommendation
      const result = await createOrderRecommendation(recommendationForm);

      onUpdateSuccess(result);

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Recommendation saved successfully!',
        },
      });
    } catch (error) {
      console.log(error);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.message || 'Recommendation failed to save...',
        },
      });
    }
    setIsSaving(false);
  };

  const updateOrderRecommendationAsync = async () => {
    //
    try {
      setIsSaving(true);

      // try to create the recommendation
      const result = await updateOrderRecommendation(recommendationForm);

      onUpdateSuccess(result);

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Recommendation saved successfully!',
        },
      });
    } catch (error) {
      console.log(error);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.message || 'Recommendation failed to save...',
        },
      });
    }
    setIsSaving(false);
  };

  const removeOrderRecommendationAsync = async () => {
    //
    try {
      setIsRemoving(true);

      // try to create the recommendation
      const result = await removeOrderRecommendationById(recommendationForm.orderRecommendationId);

      onRemoveSuccess(recommendationForm);

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Recommendation saved successfully!',
        },
      });
    } catch (error) {
      console.log(error);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.message || 'Recommendation failed to save...',
        },
      });
    }
    setIsRemoving(false);
  };

  const handleSubmitOrderRecommendation = async () => {
    // make sure recommendation and type have values
    if (!recommendationForm.recommendation.length || !recommendationForm.recommendationTypeId) {
      return dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: 'Please fill out all fields for this form',
        },
      });
    }

    // if this recommendation already exists, update it, otherwise create it
    if (activeRecommendations.find(ar => ar.recommendationLibraryId === recommendationForm.recommendationLibraryId)) {
      updateOrderRecommendationAsync();
    } else {
      createOrderRecommendationAsync();
    }
  };

  const loadRecommendationForm = () => {
    /**
     * if this changes, update the order recommendation form
     * - check if there is an active one, and use that instead of the default
     * - set the type to the first option
     * - set the recommendation string to the default string from the library
     */
    const activeRecommendation = activeRecommendations.find(ar => ar.recommendationLibraryId === selectedLibraryId);

    const values = activeRecommendation
      ? {
          // orderRecommendationId: activeRecommendation.orderRecommendationId,
          ...activeRecommendation,
          recommendationTypeId: activeRecommendation.recommendationTypeId,
          recommendation: activeRecommendation.recommendation,
          isRemovable: true,
        }
      : {
          recommendationTypeId: recommendationTypes[0].recommendationTypeId,
          recommendation: recommendationLibraries[selectedLibraryId].recommendation,
          isRemovable: false,
        };

    setRecommendationForm({
      ...recommendationFormReset,
      recommendationLibraryId: selectedLibraryId,
      ...values,
    });
  };

  useEffect(() => {
    !!selectedCategoryId && !!selectedLibraryId && loadRecommendationForm();
  }, [activeRecommendations]);

  useEffect(() => {
    if (selectedCategoryId !== null) {
      setCategoryLibraries(recommendationLibraries.filter(lib => lib.recommendationCategoryId === selectedCategoryId));
    }
    setSelectedLibraryId(null);

    // reset the form so it looks blank
    setRecommendationForm({
      ...recommendationForm,
      recommendationLibraryId: null,
      recommendation: '',
    });
  }, [selectedCategoryId]);

  useEffect(() => {
    if (selectedLibraryId === null) {
      return;
    }

    loadRecommendationForm();
  }, [selectedLibraryId]);

  return (
    <Grid item xs={12} className={classes.container}>
      <Card className={cn('border-left-green', 'flex-1', 'flex-column')}>
        <CardHeader
          className={classes.cardHeader}
          titleTypographyProps={{ className: classes.cardHeaderTitle }}
          title="Order Recommendations"
        />
        <CardContent className={classes.table}>
          {/* <div className={cn(classes.table)}> */}
          {/* first column - recommendation categories */}
          <div className={cn(classes.tableColumn)}>
            <div className={classes.tableColumnHeader}>Categories</div>
            <div className={classes.columnListContainer}>{recommendationCategories.map(renderCategoryTile)}</div>
          </div>

          {/* second column - recommendation library */}
          <div className={cn(classes.tableColumn, selectedCategoryId === null && classes.columnDisabled)}>
            <div className={classes.tableColumnHeader}>Libraries</div>
            {categoryLibraries.map(renderLibraryTile)}
          </div>

          {/* third column - editable form */}
          <div
            className={cn(
              classes.tableColumn,
              classes.recommendationFormColumn,
              [selectedCategoryId, selectedLibraryId].includes(null) && classes.columnDisabled,
              (isSaving || isRemoving) && classes.columnDisabled,
            )}
          >
            <div className={classes.tableColumnHeader}>Customize</div>
            <div className={classes.recommendationFormContainer}>
              <TextField
                id="outlined-multiline-static"
                label="Recommendation"
                multiline
                rows="10"
                value={recommendationForm.recommendation}
                variant="outlined"
                style={{
                  marginBottom: 10,
                  width: '100%',
                }}
                onChange={evt =>
                  setRecommendationForm({
                    ...recommendationForm,
                    recommendation: evt.target.value,
                  })
                }
              />

              <Autocomplete
                options={recommendationTypesOptions}
                onChange={opt =>
                  setRecommendationForm({
                    ...recommendationForm,
                    recommendationTypeId: opt.value,
                  })
                }
                value={recommendationTypesOptions.find(opt => opt.value === recommendationForm.recommendationTypeId)}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitOrderRecommendation}
                className={cn('light-blue-background')}
                style={{ marginTop: 10 }}
              >
                {isSaving ? <CircularProgress color="#fff" size={18} /> : 'Save Recommendation'}
              </Button>

              {recommendationForm.isRemovable && (
                <Button
                  variant="contained"
                  onClick={removeOrderRecommendationAsync}
                  className={cn(classes.removeButton)}
                  style={{ marginTop: 10 }}
                >
                  {isRemoving ? <CircularProgress color="#fff" size={18} /> : 'Remove Recommendation'}
                </Button>
              )}
            </div>
          </div>
          {/* </div> */}
        </CardContent>

        <CardHeader
          className={classes.cardHeader}
          titleTypographyProps={{ className: classes.cardHeaderTitle }}
          title="Existing Recommendations"
        />
        <CardContent className={classes.activeRecommendationsContainer}>
          {activeRecommendations.map(renderExistingRecommendationListItem)}
        </CardContent>
      </Card>
    </Grid>
  );
};

const styles = theme => ({
  container: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    padding: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    padding: '0',
  },
  cardHeader: {
    padding: '12px',
    backgroundColor: '#fafafa',
  },
  cardHeaderTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    textAlign: 'left',
    color: 'var(--jmi-blue)',
  },

  tableColumn: {
    border: '1px solid gray',
    flex: 1,
    alignSelf: 'stretch',
    marginRight: 5,
    marginLeft: 5,
    overflow: 'auto',
  },
  tableColumnHeader: {
    // marginTop: 15,
    // marginBottom: 15,
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    paddingTop: 15,
    paddingBottom: 15,
    fontSize: 24,
    borderBottom: '1px solid gray',
    zIndex: 2,
  },

  table: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'auto',
  },

  columnTile: {
    marginTop: 5,
    marginBottom: 5,
    padding: 10,
    // textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderLeft: '3px solid transparent',

    '&:hover': {
      backgroundColor: '#eee',
    },
  },
  columnDisabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  columnTileSelected: {
    backgroundColor: '#999',
    color: 'white',

    '&:hover': {
      color: 'black',
    },
  },
  columnTileHasOrderRecommendation: {
    borderLeft: '3px solid #5D0',
  },

  recommendationFormContainer: {
    padding: 10,
    paddingTop: 25,
  },

  removeButton: {
    marginRight: '8px',
    backgroundColor: '#f44336',
    color: '#fff',
    '&:hover': {
      background: '#d2190b',
    },
  },

  activeRecommendationsContainer: {
    maxHeight: 250,
    overflow: 'auto',
    padding: 10,
    // display: 'flex',
    // flexDirection: 'column',
  },
  activeRecommendationListItem: {
    display: 'flex',

    borderBottom: '1px solid #ccc',

    '&:last-of-type': {
      borderBottom: 'none',
    },
    // flexDirection: 'row',
    // justifyContent: 'space-between',

    // alignSelf: 'stretch',
    // overflow: 'auto',
  },

  activeRecommendationListItemColumn: {
    flex: 1,
    minWidth: 0,
    padding: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',

    // recommendation line
    '&:last-of-type': {
      flex: 3,
    },
  },

  recommendationTooltipContent: {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

OrderRecommendations.propTypes = propTypes;
export default withStyles(styles)(withRouter(OrderRecommendations));
